import { CoreFilterButton, CoreFilterSectionOption } from "@verity/components/core";
import { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ROLE_CATEGORIES, RosterFilterQuery } from "./roster-types";
import { useAppDispatch, useAppSelector } from "./store/roster-hooks";
import { setFilters } from "./store/roster-slice";

export function RosterFilterButton() {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();

	const storeFilterQuery = useAppSelector((state) => state.roster.filterQuery);

	const [filterQuery, setFilterQuery] = useState<RosterFilterQuery>({
		role: [],
		locked: [],
	});

	useEffect(() => {
		setFilterQuery({
			role: storeFilterQuery.role,
			locked: storeFilterQuery.locked,
		});
	}, [storeFilterQuery]);

	const handleClear = useCallback(() => {
		const emptyFilters: RosterFilterQuery = {
			role: [],
			locked: [],
		};
		setFilterQuery(emptyFilters);
		dispatch(setFilters(emptyFilters));
	}, [setFilterQuery, dispatch]);

	const handleApply = useCallback(() => {
		setFilterQuery({ ...filterQuery });
		dispatch(setFilters(filterQuery));
	}, [filterQuery, dispatch, setFilterQuery]);

	const filterSections: CoreFilterSectionOption[] = [
		{
			alignment: "top",
			sectionTitle: t("portal.components.roster.filter.roles.title"),
			checkboxes: ROLE_CATEGORIES.map((role) => ({
				checked: filterQuery.role.includes(role),
				onChange: (checked) => {
					if (checked) {
						const newRole = [...filterQuery.role, role];
						setFilterQuery({ ...filterQuery, role: newRole });
					} else {
						const newRole = filterQuery.role.filter((r) => r !== role);
						setFilterQuery({ ...filterQuery, role: newRole });
					}
				},
				children: t(`portal.components.roster.filter.roles.state.${role}`),
			})),
		},
		{
			alignment: "top",
			sectionTitle: t("portal.components.roster.filter.lock.title"),
			checkboxes: [
				{
					checked: filterQuery.locked.includes(true),
					onChange: (checked) => {
						if (checked) {
							const newLock = [...filterQuery.locked, true];
							setFilterQuery({ ...filterQuery, locked: newLock });
						} else {
							const newLock = filterQuery.locked.filter((r) => r !== true);
							setFilterQuery({ ...filterQuery, locked: newLock });
						}
					},
					children: t("portal.components.roster.filter.lock.state.locked"),
				},
				{
					checked: filterQuery.locked.includes(false),
					onChange: (checked) => {
						if (checked) {
							const newLock = [...filterQuery.locked, false];
							setFilterQuery({ ...filterQuery, locked: newLock });
						} else {
							const newLock = filterQuery.locked.filter((r) => r !== false);
							setFilterQuery({ ...filterQuery, locked: newLock });
						}
					},
					children: t("portal.components.roster.filter.lock.state.unlocked"),
				},
			],
		},
	];

	return (
		<CoreFilterButton
			filterCount={filterQuery.role.length + filterQuery.locked.length}
			onClear={handleClear}
			onApply={handleApply}
			filterSections={filterSections}
		/>
	);
}

export const MemoizedRosterFilterButton = memo(RosterFilterButton);
